import {
  deleteExpenseLocal,
  getExpense,
  listExpensesLocal,
  putExpenseLocal,
  type LocalExpense,
} from '../db/expensesDb.js';
import { listComplianceLocal } from '../db/complianceDb.js';
import {
  monthlyEquivalentPence,
  type ExpenseFrequency,
} from '../lib/expenseMonthly.js';
import { formatPence } from '../lib/complianceRoll.js';
import {
  listEmptyHtml,
  listErrorHtml,
  listSkeletonHtml,
} from './listStates.js';

export type ExpensesHooks = {
  onChange: () => void;
  onBack: () => void;
  onOpenCompliance?: () => void;
};

const FREQUENCIES: ExpenseFrequency[] = [
  'weekly',
  'monthly',
  'quarterly',
  'annual',
];

const FREQ_LABEL: Record<ExpenseFrequency, string> = {
  weekly: 'Weekly',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  annual: 'Annual',
};

const CATEGORY_SUGGESTIONS = [
  'internet',
  'professional',
  'subscription',
  'food',
  'other',
];

type View = 'list' | 'edit';

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function poundsInputToPence(raw: string): number | null {
  const t = raw.trim();
  if (!t) return null;
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return null;
  const [w, f = ''] = t.split('.');
  return Number(w) * 100 + Number((f + '00').slice(0, 2));
}

function penceToPoundsInput(p: number | null): string {
  if (p == null) return '';
  return (p / 100).toFixed(2);
}

export async function mountExpenses(
  root: HTMLElement,
  hooks: ExpensesHooks,
): Promise<{ refresh: () => Promise<void> }> {
  let view: View = 'list';
  let editingId: string | null = null;

  async function refresh() {
    if (view === 'edit') await renderForm(editingId);
    else await renderList();
  }

  async function renderList() {
    root.innerHTML = `
      <section class="more-panel expenses-panel">
        <button type="button" class="btn-back" data-back>‹ More</button>
        <h1 class="screen-title">Expenses</h1>
        ${listSkeletonHtml(4, 'list-skel-row skel-renew')}
      </section>
    `;
    root.querySelector('[data-back]')?.addEventListener('click', () => {
      hooks.onBack();
    });

    let listError = false;
    let expenses: LocalExpense[] = [];
    let complianceMonthly = 0;
    let complianceRows: Array<{
      id: string;
      title: string;
      cost_pence: number | null;
      billing_frequency: string;
      monthly: number;
    }> = [];
    try {
      expenses = (await listExpensesLocal()).filter((e) => e.active);
      const compliance = (await listComplianceLocal()).filter((c) => c.active);
      complianceRows = compliance
        .map((c) => ({
          id: c.id,
          title: c.title,
          cost_pence: c.cost_pence,
          billing_frequency: c.billing_frequency,
          monthly: monthlyEquivalentPence(c.cost_pence, c.billing_frequency),
        }))
        .filter((c) => c.monthly > 0)
        .sort((a, b) => b.monthly - a.monthly);
      complianceMonthly = complianceRows.reduce((s, r) => s + r.monthly, 0);
    } catch {
      listError = true;
    }

    const operatingMonthly = expenses.reduce(
      (s, e) => s + monthlyEquivalentPence(e.amount_pence, e.frequency),
      0,
    );
    const totalMonthly = operatingMonthly + complianceMonthly;

    const expenseList =
      expenses.length === 0
        ? listEmptyHtml('No recurring expenses yet. Add subscriptions and bills.', {
            label: 'Add expense',
            dataAttr: 'data-add',
          })
        : `<ul class="renew-list expense-list">${expenses
            .map((item) => {
              const monthly = monthlyEquivalentPence(
                item.amount_pence,
                item.frequency,
              );
              return `<li class="renew-item expense-item" data-id="${item.id}">
                <button type="button" class="renew-item-main" data-edit="${item.id}">
                  <strong>${escapeHtml(item.name)}</strong>
                  <span class="muted">${escapeHtml(
                    item.category ?? 'Expense',
                  )} · ${escapeHtml(FREQ_LABEL[item.frequency])} · ${formatPence(
                    item.amount_pence,
                  )}</span>
                  <span class="renew-cost">${formatPence(monthly)} / mo</span>
                </button>
              </li>`;
            })
            .join('')}</ul>`;

    const complianceSection =
      complianceRows.length === 0
        ? ''
        : `<section class="renew-group" aria-label="Compliance premiums">
            <h2>Compliance premiums</h2>
            <p class="muted">From the compliance register — edit dates under Compliance.</p>
            <ul class="renew-list expense-list">
              ${complianceRows
                .map(
                  (c) => `<li class="renew-item expense-item">
                    <div class="renew-item-main">
                      <strong>${escapeHtml(c.title)}</strong>
                      <span class="muted">${formatPence(c.cost_pence)} · ${escapeHtml(
                        c.billing_frequency.replace('_', '-'),
                      )}</span>
                      <span class="renew-cost">${formatPence(c.monthly)} / mo</span>
                    </div>
                  </li>`,
                )
                .join('')}
            </ul>
          </section>`;

    const listBody = listError
      ? listErrorHtml()
      : `${
          expenses.length > 0 || complianceRows.length > 0
            ? `<div class="expense-total" aria-live="polite">
                <span class="muted">Monthly total</span>
                <strong>${formatPence(totalMonthly)}</strong>
                <span class="muted">${formatPence(operatingMonthly)} operating · ${formatPence(
                  complianceMonthly,
                )} compliance</span>
              </div>`
            : ''
        }
        <section class="renew-group" aria-label="Operating expenses">
          <h2>Operating</h2>
          ${
            !listError && expenses.length > 0
              ? `<button type="button" class="btn-primary btn-block" data-add>Add expense</button>`
              : ''
          }
          ${expenseList}
        </section>
        ${complianceSection}
        ${
          hooks.onOpenCompliance
            ? `<p class="muted expense-compliance-link"><button type="button" class="btn-secondary" data-go-compliance>Compliance renewals</button></p>`
            : ''
        }`;

    root.innerHTML = `
      <section class="more-panel expenses-panel">
        <button type="button" class="btn-back" data-back>‹ More</button>
        <h1 class="screen-title">Expenses</h1>
        <p class="muted">Recurring costs normalised to a monthly figure. Weekly ×52÷12, quarterly ÷3, annual ÷12.</p>
        ${listBody}
      </section>
    `;

    root.querySelector('[data-back]')?.addEventListener('click', () => {
      hooks.onBack();
    });
    root.querySelectorAll('[data-add]').forEach((el) => {
      el.addEventListener('click', () => {
        editingId = null;
        view = 'edit';
        void refresh();
      });
    });
    root.querySelector('[data-list-retry]')?.addEventListener('click', () => {
      void renderList();
    });
    root.querySelectorAll('[data-edit]').forEach((el) => {
      el.addEventListener('click', () => {
        editingId = (el as HTMLElement).dataset.edit!;
        view = 'edit';
        void refresh();
      });
    });
    root.querySelector('[data-go-compliance]')?.addEventListener('click', () => {
      hooks.onOpenCompliance?.();
    });
  }

  async function renderForm(id: string | null) {
    const existing = id ? await getExpense(id) : null;
    const title = existing ? 'Edit expense' : 'Add expense';

    root.innerHTML = `
      <section class="more-panel">
        <button type="button" class="btn-back" data-cancel>‹ Expenses</button>
        <h1 class="screen-title">${title}</h1>
        <form id="expense-form" novalidate>
          <label class="field"><span>Name</span>
            <input name="name" required maxlength="500" value="${escapeHtml(existing?.name ?? '')}" />
          </label>
          <label class="field"><span>Category</span>
            <input name="category" maxlength="200" list="expense-cats" value="${escapeHtml(existing?.category ?? '')}" placeholder="subscription, internet…" />
            <datalist id="expense-cats">
              ${CATEGORY_SUGGESTIONS.map((c) => `<option value="${c}"></option>`).join('')}
            </datalist>
          </label>
          <label class="field"><span>Amount (£)</span>
            <input name="amount_pounds" required inputmode="decimal" placeholder="0.00" value="${penceToPoundsInput(
              existing?.amount_pence ?? null,
            )}" />
          </label>
          <label class="field"><span>Frequency</span>
            <select name="frequency">
              ${FREQUENCIES.map(
                (f) =>
                  `<option value="${f}" ${
                    (existing?.frequency ?? 'monthly') === f ? 'selected' : ''
                  }>${FREQ_LABEL[f]}</option>`,
              ).join('')}
            </select>
          </label>
          <label class="field"><span>Notes</span>
            <textarea name="notes" maxlength="5000" rows="3">${escapeHtml(existing?.notes ?? '')}</textarea>
          </label>
          <button class="submit" type="submit">Save</button>
          ${
            existing
              ? `<button type="button" class="btn-secondary btn-block" data-delete style="margin-top:10px">Delete</button>`
              : ''
          }
        </form>
      </section>
    `;

    root.querySelector('[data-cancel]')?.addEventListener('click', () => {
      view = 'list';
      editingId = null;
      void refresh();
    });
    root.querySelector('[data-delete]')?.addEventListener('click', async () => {
      if (!existing) return;
      if (!window.confirm(`Delete “${existing.name}”?`)) return;
      if (existing.pendingOp === 'create') {
        await deleteExpenseLocal(existing.id);
      } else {
        await putExpenseLocal({
          ...existing,
          deleted_at: new Date().toISOString(),
          active: false,
          pendingOp: 'delete',
          syncState: 'pending',
          updatedAt: new Date().toISOString(),
        });
      }
      view = 'list';
      editingId = null;
      hooks.onChange();
      await refresh();
    });

    root.querySelector('#expense-form')?.addEventListener('submit', async (ev) => {
      ev.preventDefault();
      const form = ev.target as HTMLFormElement;
      const fd = new FormData(form);
      const nameVal = String(fd.get('name') ?? '').trim();
      if (!nameVal) return;
      const amount = poundsInputToPence(String(fd.get('amount_pounds') ?? ''));
      if (amount == null) {
        window.alert('Enter a valid amount (e.g. 48.00).');
        return;
      }
      const now = new Date().toISOString();
      const base: LocalExpense = {
        id: existing?.id ?? crypto.randomUUID(),
        name: nameVal,
        category: String(fd.get('category') ?? '').trim() || null,
        amount_pence: amount,
        frequency: String(fd.get('frequency')) as ExpenseFrequency,
        notes: String(fd.get('notes') ?? '').trim() || null,
        active: true,
        deleted_at: null,
        syncState: 'pending',
        pendingOp: existing
          ? existing.pendingOp === 'create'
            ? 'create'
            : 'update'
          : 'create',
        failReason: null,
        updatedAt: now,
      };
      await putExpenseLocal(base);
      view = 'list';
      editingId = null;
      hooks.onChange();
      await refresh();
    });
  }

  await refresh();
  return { refresh };
}
